import {
	DeviceReport,
	DXDevice,
	DXDeviceType,
	DXIssue,
	DXValues,
	DXValuesTemplate,
	DXVariable,
	PendingValue,
	ReportRequest,
	ReportRequestParams,
	valueHistoryRetval,
	DX24hStatisticsvalue,
	DXManualLogEntry,
	DecodedLogEntry,
	SearchLogEntriesParams,
	Attachment,
} from "@/types";
import { asyncCall } from "./call";
import {
	CurrentValue,
	Device,
	DeviceConfig,
	DeviceIdentifiers,
	Issue,
	NewDevice,
	NewDeviceLog,
	ServerResponse,
	SparePartOrder,
	TLAInfo,
	Variable,
	VirtualVariable,
} from "./types";
import { apiV3Call } from "./apiV3Call";

export class DeviceConnector {
	private readonly sessionID: string;
	private readonly urlPrefix: string;

	constructor(urlPrefix: string, sessionID: string) {
		this.urlPrefix = urlPrefix;
		this.sessionID = sessionID;
	}

	/**
	 * @target "/api/set-device-config-multi"
	 * @param options
	 * @returns
	 */
	async setDeviceConfigMulti(options: {
		device: DeviceIdentifiers["device"];
		keyValuePairs: {
			[key: string]: string;
		};
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/set-device-config-multi",
			{
				session: this.sessionID,
				device: options.device,
				...options.keyValuePairs,
			}
		);
		return result;
	}

	/**
	 * @target "/api/copy-device-config"
	 * @param options
	 * @returns
	 */
	async copyDeviceConfig(options: {
		from: DeviceIdentifiers["device"];
		to: DeviceIdentifiers["device"];
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/copy-device-config",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/v3.0/get-value-history"
	 * @param options
	 * @returns
	 */
	async getValueHistory(options: {
		device: DeviceIdentifiers["device"];
		index: number;
		subindex: number;
		from: number;
		to: number;
	}) {
		const result = await apiV3Call<typeof options, valueHistoryRetval>(
			this.urlPrefix + "/api/v3.0/get-value-history",
			options
		);
		return result;
	}
	/**
	 * @target "/api/get-variables"
	 * @returns
	 */
	async getVariables() {
		const result: ServerResponse | Variable[] = await asyncCall(
			this.urlPrefix + "/api/get-variables",
			{
				session: this.sessionID,
			}
		);
		return result;
	}

	/**
	 * @target "/api/add-device"
	 * @param options
	 * @returns
	 */
	async addDevice(options: NewDevice) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/add-device",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}
	/**
	 * @target "/api/add-device-log"
	 * @param options
	 * @returns
	 */
	async addDeviceLog(options: NewDeviceLog) {
		const formData = new FormData();
		formData.append("device", options.device.toString());
		formData.append("message", options.message);
		formData.append("attachment", options.attachment);
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/add-device-log",
			{
				session: this.sessionID,
			},
			formData
		);
		return result;
	}

	/**
	 * @target "/api/add-manual-measurement"
	 * @param params
	 * @returns
	 */
	async addManualMeasurement(params: DXManualLogEntry) {
		const result = apiV3Call<DXManualLogEntry, { logEntryID: number }>(
			this.urlPrefix + "/api/v3.0/add-manual-measurement",
			params
		);
		return result;
	}

	/**
	 * @target "/api/rename-tag"
	 * @param options
	 * @returns
	 */
	async renameTag(options: { from: string; to: string }) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/rename-tag",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/add-device-tag"
	 * @param options
	 * @returns
	 */
	async addDeviceTag(options: {
		device: DeviceIdentifiers["device"];
		tag: string;
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/add-device-tag",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/remove-device-tag"
	 * @param options
	 * @returns
	 */
	async removeDeviceTag(options: {
		device: DeviceIdentifiers["device"];
		tag: string;
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/remove-device-tag",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	async getDeviceTypes() {
		const result: ServerResponse | DXDeviceType[] = await asyncCall(
			this.urlPrefix + "/api/get-device-types",
			{
				session: this.sessionID,
			}
		);
		return result;
	}

	/**
	 * @target "/api/get-all-issues"
	 * @returns
	 */
	async getAllIssues() {
		const result: ServerResponse | DXIssue[] = await asyncCall(
			this.urlPrefix + "/api/get-all-issues",
			{
				session: this.sessionID,
			}
		);
		return result;
	}

	/**
	 * @target "/api/get-current-values"
	 * @param options
	 * @returns
	 */
	async getCurrentValues(options: { device: DeviceIdentifiers["device"] }) {
		const result: CurrentValue[] = await asyncCall(
			this.urlPrefix + "/api/get-current-values",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/v3.0/get-24h-statistics"
	 * @param options
	 */
	async get24hAllVariables(options: {
		device: DeviceIdentifiers["device"];
		index?: number;
		subindex?: number;
	}) {
		const result = await apiV3Call<typeof options, DX24hStatisticsvalue>(
			this.urlPrefix + "/api/v3.0/get-24h-statistics",
			options
		);

		if (!result.success) {
			throw new Error(result.error.messageKey);
		}

		return result.data;
	}

	/**
	 * @target "/api/get-device-config"
	 * @param options
	 * @returns
	 */
	async getDeviceConfig(options: { device: DeviceIdentifiers["device"] }) {
		const result: ServerResponse | DeviceConfig = await asyncCall(
			this.urlPrefix + "/api/get-device-config",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/v3.0/search-log-entries"
	 * @param options
	 * @returns
	 */
	async searchLogEntries(options: SearchLogEntriesParams) {
		return apiV3Call<SearchLogEntriesParams, { entries: DecodedLogEntry[] }>(
			this.urlPrefix + "/api/v3.0/search-log-entries",
			options
		);
	}

	/**
	 * @target "/api/v3.0/get-log-attachment"
	 * @param options
	 * @returns
	 */
	async getLogAttachment(options: { entry: number; attachment: number }) {
		return apiV3Call<typeof options, Attachment>(
			this.urlPrefix + "/api/v3.0/get-log-attachment",
			options
		);
	}

	/**
	 * @target "/api/get-devices"
	 * @returns
	 */
	async getDevices() {
		const result: ServerResponse | DXDevice[] = await asyncCall(
			this.urlPrefix + "/api/get-devices",
			{
				session: this.sessionID,
			}
		);
		return result;
	}

	/**
	 * @target "/api/get-issues"
	 * @param options
	 * @returns
	 */
	async getIssues(options: { device: DeviceIdentifiers["device"] }) {
		const result: ServerResponse | Issue[] = await asyncCall(
			this.urlPrefix + "/api/get-issues",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/remove-device"
	 * @param options
	 * @returns
	 */
	async removeDevice(options: { id: DeviceIdentifiers["device"] }) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/remove-device",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/set-device-config"
	 * @param options
	 * @returns
	 */
	async setDeviceConfig(options: {
		device: DeviceIdentifiers["device"];
		key: string;
		value: string;
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/set-device-config",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/set-device-name"
	 * @param options
	 * @returns
	 */
	async setDeviceName(options: {
		device: DeviceIdentifiers["device"];
		name: string;
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/set-device-name",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/set-device-offline-mail"
	 * @param options
	 * @returns
	 */
	async setDeviceOfflineMail(options: {
		device: DeviceIdentifiers["device"];
		value: number;
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/set-device-offline-mail",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * @target "/api/set-device-smoothing"
	 * @param options
	 * @returns
	 */
	async setNoiseReduction(options: {
		device: DeviceIdentifiers["device"];
		value: number;
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/set-device-smoothing",
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	/**
	 * Requires identcode to fetch spare parts.
	 * @target "/api/get-spare-parts"
	 * @param options
	 * @returns
	 */
	async getSpareParts(options: { identcode: string }) {
		const result: {
			[key: string]: string;
		} = await asyncCall(this.urlPrefix + "/api/get-spare-parts", {
			...options,
		});
		return result;
	}

	/**
	 * Sends an email with the spare part list. It doesn't do any orders.
	 * @target "/api/order-spare-parts"
	 * @param options
	 * @returns
	 */
	async orderSpareParts(options: {
		device: DeviceIdentifiers["device"];
		email: string;
		items: SparePartOrder[];
	}) {
		const result: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/order-spare-parts",
			{},
			{
				session: this.sessionID,
				...options,
			}
		);
		return result;
	}

	async getDeviceLinkedTankApp(options: { device_id: number }) {
		const result: TLAInfo = await asyncCall(
			this.urlPrefix + "/api/get-device-linked-tank-app",
			{
				...options,
				session: this.sessionID,
			}
		);
		return result;
	}

	// special snowflake methods for reports, since they are special as well

	dateToReportName(d: Date) {
		const zeroPad = (v: number) => (v > 9 ? v.toString() : "0" + v);
		return `${d.getFullYear()}-${zeroPad(d.getMonth() + 1)}-${zeroPad(
			d.getDate()
		)}`;
	}

	getTagString(tag: string) {
		// the reportgenerator demands this format!
		return (tag !== "" ? `tag_${tag}` : "dash_all-devices").replace(
			/\s+/g,
			"-"
		);
	}

	getReportName(tag: string, start: Date, end: Date, userID: string) {
		const startString = this.dateToReportName(start);
		const endString = this.dateToReportName(end);
		const tagString = this.getTagString(tag);

		return `${userID}_${tagString}_${startString}_${endString}.xlsx`;
	}

	async addVirtualDevice(options: {
		description?: string;
		name: string;
		comment?: string;
		tag: string;
	}) {
		const res: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/add-virtual-device",
			{
				...options,
				session: this.sessionID,
			}
		);
		return res;
	}

	async getVirtualDevices() {
		const res: ServerResponse | Device[] = await asyncCall(
			this.urlPrefix + "/api/get-virtual-devices",
			{
				session: this.sessionID,
			}
		);
		return res;
	}

	async updateVirtualDevice(options: { name: string; id: number }) {
		const res: ServerResponse = await asyncCall(
			this.urlPrefix + "/api/update-virtual-device",
			{
				session: this.sessionID,
				...options,
			}
		);
		return res;
	}

	async deleteVirtualDevice(options: { id: number }) {
		return (await asyncCall(this.urlPrefix + "/api/delete-virtual-device", {
			session: this.sessionID,
			...options,
		})) as ServerResponse;
	}

	async addVirtualVariable(
		options: {
			deviceId: number;
			sppindex: number;
			sppsubindex: number;
			decimalPlaces: number;
			isAlarmRelevant: number;
			rank: number;
			mainRank: number;
			isWhitelisted: number;
			showPreview: number;
			varName: string;
			varGroup: string;
			unit: string;
		},
		calculationJSON: string
	) {
		return (await asyncCall(
			this.urlPrefix + "/api/add-virtual-variable",
			{
				session: this.sessionID,
				...options,
			},
			calculationJSON,
			{
				"Content-Type": "text/plain",
			}
		)) as ServerResponse;
	}

	async getVirtualVariables(options: {
		deviceId: number;
		sppindex?: number;
		sppsubindex?: number;
	}) {
		return (await asyncCall(this.urlPrefix + "/api/get-virtual-variable", {
			deviceId: options.deviceId,
			sppindex: options.sppindex ?? -1,
			sppsubindex: options.sppsubindex ?? -1,
			session: this.sessionID,
		})) as VirtualVariable[];
	}

	async updateVirtualVariable(
		options: {
			deviceId: number;
			sppindex: number;
			sppsubindex: number;
			decimalPlaces: number;
			isAlarmRelevant: number;
			rank: number;
			mainRank: number;
			isWhitelisted: number;
			showPreview: number;
			oldSppindex: number;
			oldSppsubindex: number;
			varName: string;
			varGroup: string;
			unit: string;
		},
		calculationJSON: string
	) {
		return await asyncCall(
			this.urlPrefix + "/api/update-virtual-variable",
			{
				...options,
				session: this.sessionID,
			},
			calculationJSON,
			{
				"Content-Type": "text/plain",
			}
		);
	}

	async deleteVirtualVariable(options: {
		varName: string;
		deviceId: number;
		sppindex: number;
		sppsubindex: number;
	}) {
		return (await asyncCall(this.urlPrefix + "/api/delete-virtual-variable", {
			session: this.sessionID,
			...options,
		})) as ServerResponse;
	}

	async getPendingValues(options: { deviceID: number }) {
		return (await asyncCall(this.urlPrefix + "/api/get-pending-values", {
			session: this.sessionID,
			...options,
		})) as PendingValue[];
	}

	async getPredefinedCalculations() {
		const predefinedCalculations: any = await asyncCall(
			this.urlPrefix + "/api/get-predetermined-calculations",
			{
				session: this.sessionID,
			}
		);
		return predefinedCalculations;
	}

	async isDeviceUsedByVirtualVariables(options: { deviceId: number }) {
		return (await asyncCall(
			this.urlPrefix + "/api/is-device-used-by-virtual-variable",
			{
				deviceId: options.deviceId,
				session: this.sessionID,
			}
		)) as string;
	}

	async getValuesTemplates() {
		return (await asyncCall(
			this.urlPrefix + "/api/get-user-devicetype-variables-mappings",
			{
				session: this.sessionID,
			}
		)) as DXValuesTemplate[];
	}

	async setValuesTemplates(options: {
		name: string;
		deviceType: string;
		values: DXValues[];
	}) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/create-user-devicetype-variables-mapping",
			{
				...options,
				session: this.sessionID,
			},
			options.values
		);
		return result;
	}

	async updateValuesTemplates(options: {
		mappingId: number;
		name: string;
		deviceType: string;
		values: DXValues[];
	}) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/update-user-devicetype-variables-mapping",
			{
				...options,
				session: this.sessionID,
			},
			options.values
		);
		return result;
	}

	async deleteValuesTemplate(mappingId: number) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/delete-user-devicetype-variables-mapping",
			{
				mappingId,
				session: this.sessionID,
			}
		);
		return result;
	}

	async getReportRequests() {
		return (await asyncCall(
			this.urlPrefix + "/api/get-device-report-requests",
			{
				session: this.sessionID,
			}
		)) as ReportRequest[];
	}

	async createReportRequest(options: ReportRequestParams): Promise<number> {
		const result = await asyncCall(
			this.urlPrefix + "/api/create-device-report-request",
			{
				session: this.sessionID,
			},
			options
		);
		return result;
	}

	async updateReportRequest(options: ReportRequestParams, requestId: number) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/edit-device-report-request",
			{
				requestId,
				session: this.sessionID,
			},
			options
		);
		return result;
	}

	async deleteReportRequest(requestId: number) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/delete-device-report-request",
			{
				requestId,
				session: this.sessionID,
			}
		);
		return result;
	}

	async activateDeactivateReportRequest(requestId: number, isActive: boolean) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/activate-deactivate-device-report-request",
			{
				requestId,
				isActive: isActive ? 0 : 1,
				session: this.sessionID,
			}
		);
		return result;
	}

	async getDeviceReports() {
		return (await asyncCall(
			this.urlPrefix + "/api/get-list-of-generated-reports",
			{
				session: this.sessionID,
			}
		)) as DeviceReport[];
	}

	async deleteDeviceReport(reportId: number) {
		const result: ServerResponse | boolean = await asyncCall(
			this.urlPrefix + "/api/delete-device-report",
			{
				reportId,
				session: this.sessionID,
			}
		);
		return result;
	}

	async getVariablesByDeviceType(deviceType: string) {
		return (await asyncCall(this.urlPrefix + "/api/get-device-type-variables", {
			session: this.sessionID,
			type: deviceType,
		})) as DXVariable[];
	}
}
